import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal, Form, OverlayTrigger, Tooltip as BsTooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  ChevronLeft, Plus, Pencil, Trash2, AlertTriangle, AlertCircle, Search,
  Activity, Coins, Users as UsersIcon, Timer, TrendingUp, TrendingDown,
  Filter, RefreshCw, Calendar, ArrowUpDown, Zap,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from "recharts";
import AiUsageService, { QuotaData } from "../../_services/AiUsageService";
import { getChartPalette, getChartTheme, useThemeVersion } from "../../theme/chartTheme";
import "./ai-usage-dashboard.css";

const FEATURE_KEYS = [
  "doubt-clarification",
  "workspace-message",
  "teacher-prompt",
  "test-prompt",
  "analyze-prompt",
  "improve-prompt",
  "post-class-recommendations",
  "course-generate",
  "resource-content-generate",
  "ai-suggest-tags",
  "analyze-module",
  "workspace-config-test",
  "workspace-config-analyze",
];

const PERIOD_PRESETS: { key: string; label: string; days: number }[] = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "all", label: "All time", days: 0 },
];

const formatNumber = (num: number | undefined | null): string => {
  if (num == null || isNaN(Number(num))) return "0";
  const n = Number(num);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
};

const formatFullNumber = (num: number | undefined | null): string => {
  if (num == null || isNaN(Number(num))) return "0";
  return Number(num).toLocaleString();
};

const formatFeatureName = (key: string | null | undefined): string => {
  if (!key) return "All Features";
  return key
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatQuotaType = (key: string): string => {
  if (key === "user_daily") return "User · Daily";
  if (key === "tenant_monthly") return "Tenant · Monthly";
  return key.replace(/_/g, " · ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatPeriod = (key: string): string =>
  key ? key.charAt(0).toUpperCase() + key.slice(1) : "—";

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
};

const isoDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const computeDelta = (current: number, previous: number): { pct: number; dir: "up" | "down" | "flat" } => {
  if (!previous) return { pct: 0, dir: "flat" };
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.1) return { pct: 0, dir: "flat" };
  return { pct, dir: pct > 0 ? "up" : "down" };
};

// ===================== Skeleton Components =====================

const Skeleton: React.FC<{ width?: string | number; height?: string | number; radius?: number; className?: string }> = ({
  width = "100%", height = 16, radius = 6, className = "",
}) => (
  <div
    className={`aiud-skel ${className}`}
    style={{ width, height, borderRadius: radius }}
  />
);

const KpiSkeleton: React.FC = () => (
  <div className="aiud-kpi-row">
    {[0, 1, 2, 3].map((i) => (
      <div className="aiud-kpi-card" key={i}>
        <Skeleton width={90} height={11} />
        <div style={{ marginTop: 14 }}><Skeleton width={120} height={28} /></div>
        <div style={{ marginTop: 12 }}><Skeleton width={70} height={12} /></div>
      </div>
    ))}
  </div>
);

const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({ rows = 5, cols = 5 }) => (
  <div className="aiud-table-skel">
    {Array.from({ length: rows }).map((_, r) => (
      <div className="aiud-table-skel-row" key={r}>
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} height={14} width={c === 0 ? "30%" : "16%"} />
        ))}
      </div>
    ))}
  </div>
);

// ===================== Component =====================

type UserSortKey = "name" | "requests" | "tokens" | "lastActive";
type SortDir = "asc" | "desc";

const AiUsageDashboard: React.FC = () => {
  // Token-resolved chart colors — recomputed on light/dark toggle
  const themeVersion = useThemeVersion();
  const chartColors = useMemo(() => getChartPalette(), [themeVersion]);
  const chartTheme = useMemo(() => getChartTheme(), [themeVersion]);
  const history = useHistory();

  // --- Period selector ---
  const [periodKey, setPeriodKey] = useState<string>("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  // Resolved applied dates (derived from period selector)
  const { appliedStartDate, appliedEndDate } = useMemo(() => {
    if (periodKey === "custom") {
      return { appliedStartDate: customStart, appliedEndDate: customEnd };
    }
    const preset = PERIOD_PRESETS.find((p) => p.key === periodKey);
    if (!preset || preset.days === 0) return { appliedStartDate: "", appliedEndDate: "" };
    return { appliedStartDate: isoDaysAgo(preset.days), appliedEndDate: todayIso() };
  }, [periodKey, customStart, customEnd]);

  // Previous period for delta computation
  const previousPeriod = useMemo(() => {
    const preset = PERIOD_PRESETS.find((p) => p.key === periodKey);
    if (!preset || preset.days === 0) return null;
    return {
      startDate: isoDaysAgo(preset.days * 2),
      endDate: isoDaysAgo(preset.days),
    };
  }, [periodKey]);

  // --- Tenant Monthly Overview ---
  const [tenantData, setTenantData] = useState<any>(null);
  const [previousData, setPreviousData] = useState<any>(null);
  const [tenantLoading, setTenantLoading] = useState(true);

  // --- Trend Chart ---
  const [trendData, setTrendData] = useState<any[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);

  // --- Per-User Usage ---
  const [userUsage, setUserUsage] = useState<any[]>([]);
  const [userUsageTotal, setUserUsageTotal] = useState(0);
  const [userUsageLoading, setUserUsageLoading] = useState(false);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userSortKey, setUserSortKey] = useState<UserSortKey>("tokens");
  const [userSortDir, setUserSortDir] = useState<SortDir>("desc");
  const USER_PAGE_SIZE = 20;

  // --- Feature Breakdown ---
  const [featureData, setFeatureData] = useState<any[]>([]);
  const [featureLoading, setFeatureLoading] = useState(true);

  // --- Quotas ---
  const [quotas, setQuotas] = useState<any[]>([]);
  const [quotasLoading, setQuotasLoading] = useState(true);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [editingQuota, setEditingQuota] = useState<QuotaData | null>(null);
  const [quotaForm, setQuotaForm] = useState<QuotaData>({
    quotaType: "user_daily",
    userId: null,
    feature: null,
    maxRequests: null,
    maxTokens: null,
    maxEstimatedCostUsd: null,
    period: "daily",
  });
  const [quotaSaving, setQuotaSaving] = useState(false);

  // --- Confirm Delete Modal ---
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // --- Error Modal ---
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ===== Fetch: Overview (current + previous period for delta) =====
  const loadOverview = useCallback(async () => {
    setTenantLoading(true);
    const currentParams: any = {};
    if (appliedStartDate) currentParams.startDate = appliedStartDate;
    if (appliedEndDate) currentParams.endDate = appliedEndDate;

    const calls: Promise<any>[] = [
      AiUsageService.getUsageSummary(currentParams),
      AiUsageService.getTenantMonthlyUsage(),
    ];
    if (previousPeriod) {
      calls.push(AiUsageService.getUsageSummary(previousPeriod));
    }

    const results = await Promise.all(calls);
    const [summaryResult, quotaResult, previousResult] = results;

    const overview: any = {};
    if (summaryResult.success && summaryResult.data) {
      const row = Array.isArray(summaryResult.data) ? summaryResult.data[0] : summaryResult.data;
      if (row) {
        overview.total_requests = row.total_requests;
        overview.total_tokens = row.total_tokens;
        overview.total_input_tokens = row.total_input_tokens;
        overview.total_cache_read_tokens = row.total_cache_read_tokens;
        overview.total_cache_creation_tokens = row.total_cache_creation_tokens;
        overview.unique_users = row.unique_users;
        overview.avg_processing_ms = row.avg_processing_ms;
      }
    }
    if (quotaResult.success && quotaResult.data) {
      const q = quotaResult.data.quota || quotaResult.data;
      overview.max_tokens = q.max_tokens || q.maxTokens || 0;
      overview.max_requests = q.max_requests || q.maxRequests || 0;
    }
    setTenantData(overview);

    if (previousResult?.success && previousResult.data) {
      const prevRow = Array.isArray(previousResult.data) ? previousResult.data[0] : previousResult.data;
      setPreviousData(prevRow || null);
    } else {
      setPreviousData(null);
    }

    setTenantLoading(false);
  }, [appliedStartDate, appliedEndDate, previousPeriod]);

  // ===== Fetch: Trend =====
  const loadTrend = useCallback(async () => {
    setTrendLoading(true);
    const params: any = {};
    if (appliedStartDate) params.startDate = appliedStartDate;
    if (appliedEndDate) params.endDate = appliedEndDate;
    const result = await AiUsageService.getDailyTrend(params);
    if (result.success && result.data) {
      const rows = Array.isArray(result.data) ? result.data : [];
      // Backend returns descending dates — flip to ascending for chart
      const sorted = [...rows]
        .map((r: any) => ({
          date: r.date,
          requests: Number(r.total_requests) || 0,
          tokens: Number(r.total_tokens) || 0,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setTrendData(sorted);
    } else {
      setTrendData([]);
    }
    setTrendLoading(false);
  }, [appliedStartDate, appliedEndDate]);

  // ===== Fetch: User Usage =====
  const loadUserUsage = useCallback(async () => {
    setUserUsageLoading(true);
    const params: any = {
      limit: USER_PAGE_SIZE,
      offset: userPage * USER_PAGE_SIZE,
    };
    if (appliedStartDate) params.startDate = appliedStartDate;
    if (appliedEndDate) params.endDate = appliedEndDate;
    const result = await AiUsageService.getUserUsageDetails(params);
    if (result.success && result.data) {
      const users = Array.isArray(result.data) ? result.data : (result.data.users || []);
      setUserUsage(users);
      setUserUsageTotal(Array.isArray(result.data) ? users.length : (result.data.total || 0));
    } else {
      setUserUsage([]);
      setUserUsageTotal(0);
    }
    setUserUsageLoading(false);
  }, [userPage, appliedStartDate, appliedEndDate]);

  // ===== Fetch: Feature Breakdown =====
  const loadFeatureBreakdown = useCallback(async () => {
    setFeatureLoading(true);
    const params: any = { groupBy: "feature" };
    if (appliedStartDate) params.startDate = appliedStartDate;
    if (appliedEndDate) params.endDate = appliedEndDate;
    const result = await AiUsageService.getUsageSummary(params);
    if (result.success && result.data) {
      const features = Array.isArray(result.data) ? result.data : (result.data.features || []);
      setFeatureData(features);
    } else {
      setFeatureData([]);
    }
    setFeatureLoading(false);
  }, [appliedStartDate, appliedEndDate]);

  // ===== Fetch: Quotas =====
  const loadQuotas = useCallback(async () => {
    setQuotasLoading(true);
    const result = await AiUsageService.getQuotas();
    if (result.success && result.data) {
      setQuotas(Array.isArray(result.data) ? result.data : result.data.quotas || []);
    } else {
      setQuotas([]);
    }
    setQuotasLoading(false);
  }, []);

  // ===== Effects =====
  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadTrend(); }, [loadTrend]);
  useEffect(() => { loadUserUsage(); }, [loadUserUsage]);
  useEffect(() => { loadFeatureBreakdown(); }, [loadFeatureBreakdown]);
  useEffect(() => { loadQuotas(); }, [loadQuotas]);

  const refreshAll = () => {
    loadOverview();
    loadTrend();
    loadUserUsage();
    loadFeatureBreakdown();
    loadQuotas();
  };

  // ===== Period selector handlers =====
  const onPeriodChange = (key: string) => {
    if (key === "custom") {
      setShowCustom(true);
      return;
    }
    setPeriodKey(key);
    setUserPage(0);
  };

  const applyCustomRange = () => {
    if (!customStart && !customEnd) {
      setShowCustom(false);
      return;
    }
    setPeriodKey("custom");
    setUserPage(0);
    setShowCustom(false);
  };

  // ===== Quota Modal handlers =====
  const openAddQuota = () => {
    setEditingQuota(null);
    setQuotaForm({
      quotaType: "user_daily",
      userId: null,
      feature: null,
      maxRequests: null,
      maxTokens: null,
      maxEstimatedCostUsd: null,
      period: "daily",
    });
    setShowQuotaModal(true);
  };

  const openEditQuota = (q: any) => {
    const rawCost = q.maxEstimatedCostUsd ?? q.max_estimated_cost_usd ?? null;
    setEditingQuota(q);
    setQuotaForm({
      id: q.id,
      quotaType: q.quotaType || q.quota_type || "user_daily",
      userId: q.userId || q.user_id || null,
      feature: q.feature || null,
      maxRequests: q.maxRequests || q.max_requests || null,
      maxTokens: q.maxTokens || q.max_tokens || null,
      maxEstimatedCostUsd: rawCost !== null && rawCost !== undefined ? Number(rawCost) : null,
      period: q.period || "daily",
    });
    setShowQuotaModal(true);
  };

  const handleQuotaSave = async () => {
    setQuotaSaving(true);
    const result = await AiUsageService.upsertQuota(quotaForm);
    setQuotaSaving(false);
    if (result.success) {
      setShowQuotaModal(false);
      loadQuotas();
    } else {
      setErrorMessage(result.error || "Failed to save quota");
    }
  };

  const confirmDeleteQuota = (id: number) => {
    setDeleteTarget(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteQuota = async () => {
    if (deleteTarget == null) return;
    setDeleteLoading(true);
    const result = await AiUsageService.deleteQuota(deleteTarget);
    setDeleteLoading(false);
    if (result.success) {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      loadQuotas();
    } else {
      setShowDeleteConfirm(false);
      setErrorMessage(result.error || "Failed to delete quota");
    }
  };

  // ===== Derived values =====
  const totalRequests = Number(tenantData?.total_requests) || 0;
  const totalTokens = Number(tenantData?.total_tokens) || 0;
  const uniqueUsers = Number(tenantData?.unique_users) || 0;
  const avgResponseTime = Number(tenantData?.avg_processing_ms) || 0;
  const tokenBudget = Number(tenantData?.max_tokens) || 0;
  const tokenPct = tokenBudget > 0 ? Math.min((totalTokens / tokenBudget) * 100, 100) : 0;

  // Anthropic prompt-cache stats. `input_tokens` from Anthropic is only the
  // uncached prompt portion — the "true" input size = uncached + cache_read +
  // cache_creation. Hit rate is what fraction of prompt tokens were served
  // from cache (billed at ~10% of normal). Cache_creation is billed at ~125%,
  // so we count it separately in the denominator, not as a "hit".
  const cacheReadTokens = Number(tenantData?.total_cache_read_tokens) || 0;
  const cacheCreationTokens = Number(tenantData?.total_cache_creation_tokens) || 0;
  const uncachedInputTokens = Number(tenantData?.total_input_tokens) || 0;
  const truePromptTokens = uncachedInputTokens + cacheReadTokens + cacheCreationTokens;
  const cacheHitPct = truePromptTokens > 0 ? (cacheReadTokens / truePromptTokens) * 100 : 0;

  const prevRequests = Number(previousData?.total_requests) || 0;
  const prevTokens = Number(previousData?.total_tokens) || 0;
  const prevUsers = Number(previousData?.unique_users) || 0;
  const prevAvgMs = Number(previousData?.avg_processing_ms) || 0;

  const requestsDelta = computeDelta(totalRequests, prevRequests);
  const tokensDelta = computeDelta(totalTokens, prevTokens);
  const usersDelta = computeDelta(uniqueUsers, prevUsers);
  // For avg response time, "down" is good
  const avgMsDelta = computeDelta(avgResponseTime, prevAvgMs);

  const periodLabel = useMemo(() => {
    if (periodKey === "custom") {
      if (customStart || customEnd) return `${customStart || "—"} → ${customEnd || "Today"}`;
      return "Custom";
    }
    return PERIOD_PRESETS.find((p) => p.key === periodKey)?.label || "Last 30 days";
  }, [periodKey, customStart, customEnd]);

  // Ranked feature data
  const rankedFeatures = useMemo(() => {
    const enriched = featureData.map((f: any) => ({
      feature: f.feature || f.name || "unknown",
      tokens: Number(f.total_tokens || f.totalTokens || f.tokens) || 0,
      requests: Number(f.total_requests || f.totalRequests || f.requests) || 0,
    }));
    enriched.sort((a, b) => b.tokens - a.tokens);
    const max = enriched.length ? enriched[0]!.tokens : 0;
    return { rows: enriched, max };
  }, [featureData]);

  // Filtered + sorted user data
  const visibleUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const normalized = userUsage.map((u: any) => {
      const name = u.student_name || u.userName || u.user_name || u.name || null;
      return {
        raw: u,
        name,
        requests: Number(u.total_requests || u.totalRequests) || 0,
        tokens: Number(u.total_tokens || u.totalTokens) || 0,
        lastActive: u.last_active || u.lastActive || null,
        featuresUsed: u.features_used ?? u.featuresUsed,
      };
    });
    const filtered = q
      ? normalized.filter((u) => (u.name || "").toLowerCase().includes(q))
      : normalized;

    const sorted = [...filtered].sort((a, b) => {
      const dir = userSortDir === "asc" ? 1 : -1;
      if (userSortKey === "name") return dir * (a.name || "").localeCompare(b.name || "");
      if (userSortKey === "requests") return dir * (a.requests - b.requests);
      if (userSortKey === "tokens") return dir * (a.tokens - b.tokens);
      if (userSortKey === "lastActive") {
        const av = a.lastActive ? new Date(a.lastActive).getTime() : 0;
        const bv = b.lastActive ? new Date(b.lastActive).getTime() : 0;
        return dir * (av - bv);
      }
      return 0;
    });
    return sorted;
  }, [userUsage, userSearch, userSortKey, userSortDir]);

  const toggleSort = (key: UserSortKey) => {
    if (userSortKey === key) {
      setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setUserSortKey(key);
      setUserSortDir(key === "name" ? "asc" : "desc");
    }
  };

  // Chart tooltip
  const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="aiud-chart-tooltip">
        <div className="aiud-chart-tooltip-date">
          {new Date(label).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        </div>
        {payload.map((p: any) => (
          <div key={p.dataKey} className="aiud-chart-tooltip-row">
            <span className="aiud-chart-tooltip-dot" style={{ background: p.color }} />
            <span className="aiud-chart-tooltip-name">{p.name}</span>
            <span className="aiud-chart-tooltip-value">{formatFullNumber(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderDelta = (delta: { pct: number; dir: "up" | "down" | "flat" }, inverted = false) => {
    if (delta.dir === "flat") {
      return <span className="aiud-delta aiud-delta-flat">No change</span>;
    }
    const goodDirection = inverted ? "down" : "up";
    const isGood = delta.dir === goodDirection;
    const Icon = delta.dir === "up" ? TrendingUp : TrendingDown;
    return (
      <span className={`aiud-delta ${isGood ? "aiud-delta-good" : "aiud-delta-bad"}`}>
        <Icon size={12} strokeWidth={2.5} />
        {Math.abs(delta.pct).toFixed(1)}% vs prev
      </span>
    );
  };

  return (
    <div className="ai-usage-dashboard">
      {/* ========== Hero Header ========== */}
      <div className="aiud-hero">
        <button className="aiud-breadcrumb" onClick={() => history.push("/tenant-preferences")}>
          <ChevronLeft size={14} strokeWidth={2.5} />
          Tenant Preferences
        </button>
        <div className="aiud-hero-row">
          <div className="aiud-hero-titles">
            <h1 className="aiud-hero-title">AI Usage Dashboard</h1>
            <p className="aiud-hero-subtitle">
              Monitor token consumption, manage quotas, and track per-user AI spend across your tenant.
            </p>
            <button
              className="aiud-related-link"
              onClick={() => history.push("/tenant-preferences?tab=ai-providers")}
            >
              AI Providers — keys, connection health &amp; spend cap →
            </button>
          </div>
          <div className="aiud-hero-actions">
            <div className="aiud-period-selector">
              <Calendar size={14} className="aiud-period-icon" />
              <Form.Select
                size="sm"
                value={periodKey}
                onChange={(e) => onPeriodChange(e.target.value)}
                className="aiud-period-select"
              >
                {PERIOD_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
                <option value="custom">Custom range…</option>
              </Form.Select>
            </div>
            <OverlayTrigger placement="bottom" overlay={<BsTooltip id="aiud-refresh">Refresh all data</BsTooltip>}>
              <button className="aiud-icon-btn" onClick={refreshAll} aria-label="Refresh">
                <RefreshCw size={15} />
              </button>
            </OverlayTrigger>
            <Button variant="primary" size="sm" onClick={openAddQuota} className="aiud-primary-btn">
              <Plus size={14} strokeWidth={2.5} /> Add Quota
            </Button>
          </div>
        </div>
        {periodKey === "custom" && (appliedStartDate || appliedEndDate) && (
          <div className="aiud-period-chip">
            <Filter size={12} />
            <span>{periodLabel}</span>
          </div>
        )}
      </div>

      {/* ========== Quota Warning Banner ========== */}
      {!tenantLoading && tokenBudget > 0 && tokenPct >= 75 && (
        <div className={`aiud-warning ${tokenPct >= 90 ? "aiud-warning-red" : "aiud-warning-amber"}`}>
          {tokenPct >= 90 ? <AlertTriangle size={20} /> : <AlertCircle size={20} />}
          <div className="aiud-warning-body">
            {tokenPct >= 100 ? (
              <>
                <strong>Token quota exceeded.</strong> AI features are blocked for students until the quota is increased or the billing period resets.
              </>
            ) : tokenPct >= 90 ? (
              <>
                <strong>Token usage at {tokenPct.toFixed(1)}%.</strong> AI features will be blocked for students once the quota is fully consumed. Consider increasing the limit.
              </>
            ) : (
              <>
                <strong>Token usage at {tokenPct.toFixed(1)}%.</strong> Approaching the monthly quota limit.
              </>
            )}
          </div>
        </div>
      )}

      {/* ========== KPI Row ========== */}
      {tenantLoading ? <KpiSkeleton /> : (
        <div className="aiud-kpi-row">
          <div className="aiud-kpi-card aiud-kpi-indigo">
            <div className="aiud-kpi-header">
              <div className="aiud-kpi-label">Total Requests</div>
              <div className="aiud-kpi-icon"><Activity size={16} /></div>
            </div>
            <div className="aiud-kpi-value">{formatNumber(totalRequests)}</div>
            <div className="aiud-kpi-footer">{previousPeriod ? renderDelta(requestsDelta) : <span className="aiud-delta aiud-delta-flat">{periodLabel}</span>}</div>
          </div>

          <div className="aiud-kpi-card aiud-kpi-teal">
            <div className="aiud-kpi-header">
              <div className="aiud-kpi-label">{tokenBudget > 0 ? "Tokens / Budget" : "Total Tokens"}</div>
              <div className="aiud-kpi-icon"><Coins size={16} /></div>
            </div>
            <div className="aiud-kpi-value">
              {formatNumber(totalTokens)}
              {tokenBudget > 0 && (
                <span className="aiud-kpi-value-sub"> / {formatNumber(tokenBudget)}</span>
              )}
            </div>
            {tokenBudget > 0 ? (
              <div className="aiud-kpi-progress-wrap">
                <div className="aiud-kpi-progress">
                  <div
                    className={`aiud-kpi-progress-fill ${tokenPct >= 90 ? "aiud-progress-red" : tokenPct >= 75 ? "aiud-progress-amber" : "aiud-progress-green"}`}
                    style={{ width: `${tokenPct}%` }}
                  />
                </div>
                <span className="aiud-kpi-progress-pct">{tokenPct.toFixed(1)}%</span>
              </div>
            ) : (
              <div className="aiud-kpi-footer">{previousPeriod ? renderDelta(tokensDelta) : <span className="aiud-delta aiud-delta-flat">{periodLabel}</span>}</div>
            )}
          </div>

          <div className="aiud-kpi-card aiud-kpi-rose">
            <div className="aiud-kpi-header">
              <div className="aiud-kpi-label">Unique Users</div>
              <div className="aiud-kpi-icon"><UsersIcon size={16} /></div>
            </div>
            <div className="aiud-kpi-value">{formatNumber(uniqueUsers)}</div>
            <div className="aiud-kpi-footer">{previousPeriod ? renderDelta(usersDelta) : <span className="aiud-delta aiud-delta-flat">{periodLabel}</span>}</div>
          </div>

          <div className="aiud-kpi-card aiud-kpi-amber">
            <div className="aiud-kpi-header">
              <div className="aiud-kpi-label">Avg Response Time</div>
              <div className="aiud-kpi-icon"><Timer size={16} /></div>
            </div>
            <div className="aiud-kpi-value">
              {avgResponseTime ? `${avgResponseTime.toFixed(0)}` : "—"}
              {avgResponseTime > 0 && <span className="aiud-kpi-value-sub"> ms</span>}
            </div>
            <div className="aiud-kpi-footer">{previousPeriod ? renderDelta(avgMsDelta, true) : <span className="aiud-delta aiud-delta-flat">{periodLabel}</span>}</div>
          </div>

          <OverlayTrigger
            placement="bottom"
            overlay={
              <BsTooltip id="aiud-cache-tt">
                Fraction of prompt tokens served from Anthropic prompt cache (billed at ~10% of input).
                Uncached input: {formatFullNumber(uncachedInputTokens)}
                {" · "}Cached (read): {formatFullNumber(cacheReadTokens)}
                {" · "}Cache written: {formatFullNumber(cacheCreationTokens)}.
                Only meaningful for stateful features (AI workspace chat, multi-turn tools).
              </BsTooltip>
            }
          >
            <div className="aiud-kpi-card aiud-kpi-lime">
              <div className="aiud-kpi-header">
                <div className="aiud-kpi-label">Cache Hit Rate</div>
                <div className="aiud-kpi-icon"><Zap size={16} /></div>
              </div>
              <div className="aiud-kpi-value">
                {truePromptTokens > 0 ? `${cacheHitPct.toFixed(1)}` : "—"}
                {truePromptTokens > 0 && <span className="aiud-kpi-value-sub"> %</span>}
              </div>
              <div className="aiud-kpi-footer">
                {truePromptTokens > 0 ? (
                  <span className="aiud-delta aiud-delta-flat">
                    {formatNumber(cacheReadTokens)} cached · saves ~{formatNumber(Math.round(cacheReadTokens * 0.9))} input
                  </span>
                ) : (
                  <span className="aiud-delta aiud-delta-flat">{periodLabel}</span>
                )}
              </div>
            </div>
          </OverlayTrigger>
        </div>
      )}

      {/* ========== Trend Chart ========== */}
      <div className="aiud-card">
        <div className="aiud-card-header">
          <div>
            <div className="aiud-card-title">Usage Trend</div>
            <div className="aiud-card-subtitle">Requests and tokens per day — {periodLabel}</div>
          </div>
        </div>
        {trendLoading ? (
          <div style={{ padding: "32px 8px" }}>
            <Skeleton height={280} radius={12} />
          </div>
        ) : trendData.length === 0 ? (
          <div className="aiud-empty">
            <Activity size={32} strokeWidth={1.5} />
            <div>No usage data in this period</div>
          </div>
        ) : (
          <div className="aiud-chart-wrap">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="aiudReq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors[0]} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={chartColors[0]} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="aiudTok" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors[4]} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={chartColors[4]} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  tick={{ fontSize: 11, fill: chartTheme.axisText }}
                  axisLine={{ stroke: chartTheme.grid }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11, fill: chartTheme.axisText }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: chartTheme.axisText }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <RTooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ paddingTop: 8, fontSize: 12 }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="requests"
                  name="Requests"
                  stroke={chartColors[0]}
                  strokeWidth={2}
                  fill="url(#aiudReq)"
                  activeDot={{ r: 4 }}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="tokens"
                  name="Tokens"
                  stroke={chartColors[4]}
                  strokeWidth={2}
                  fill="url(#aiudTok)"
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ========== Two-column: Features + Users ========== */}
      <div className="aiud-grid-2">
        {/* Per-Feature Breakdown */}
        <div className="aiud-card">
          <div className="aiud-card-header">
            <div>
              <div className="aiud-card-title">Top Features by Tokens</div>
              <div className="aiud-card-subtitle">Where consumption is concentrated</div>
            </div>
          </div>
          {featureLoading ? (
            <div style={{ padding: "8px 0" }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0" }}>
                  <Skeleton width={140} height={12} />
                  <Skeleton height={10} />
                </div>
              ))}
            </div>
          ) : rankedFeatures.rows.length === 0 ? (
            <div className="aiud-empty">
              <Activity size={28} strokeWidth={1.5} />
              <div>No feature usage data</div>
            </div>
          ) : (
            <div className="aiud-feature-bars">
              {rankedFeatures.rows.map((f, idx) => {
                const pct = rankedFeatures.max > 0 ? (f.tokens / rankedFeatures.max) * 100 : 0;
                return (
                  <div className="aiud-feature-bar" key={f.feature}>
                    <div className="aiud-feature-bar-head">
                      <div className="aiud-feature-bar-rank">{idx + 1}</div>
                      <div className="aiud-feature-bar-name" title={formatFeatureName(f.feature)}>
                        {formatFeatureName(f.feature)}
                      </div>
                      <div className="aiud-feature-bar-value">{formatNumber(f.tokens)}</div>
                    </div>
                    <div className="aiud-feature-bar-track">
                      <div className="aiud-feature-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="aiud-feature-bar-meta">{formatNumber(f.requests)} requests</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Per-User Usage */}
        <div className="aiud-card">
          <div className="aiud-card-header">
            <div>
              <div className="aiud-card-title">Per-User Usage</div>
              <div className="aiud-card-subtitle">Click a column to sort</div>
            </div>
            <div className="aiud-search-input">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search users…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
          </div>
          {userUsageLoading ? (
            <TableSkeleton rows={6} cols={4} />
          ) : visibleUsers.length === 0 ? (
            <div className="aiud-empty">
              <UsersIcon size={28} strokeWidth={1.5} />
              <div>{userSearch ? "No users match your search" : "No usage data in this period"}</div>
            </div>
          ) : (
            <>
              <table className="aiud-table">
                <thead>
                  <tr>
                    <th onClick={() => toggleSort("name")} className="aiud-sortable">
                      <span>User</span>
                      <ArrowUpDown size={11} className={userSortKey === "name" ? "aiud-sort-active" : ""} />
                    </th>
                    <th onClick={() => toggleSort("requests")} className="aiud-sortable aiud-num">
                      <span>Requests</span>
                      <ArrowUpDown size={11} className={userSortKey === "requests" ? "aiud-sort-active" : ""} />
                    </th>
                    <th onClick={() => toggleSort("tokens")} className="aiud-sortable aiud-num">
                      <span>Tokens</span>
                      <ArrowUpDown size={11} className={userSortKey === "tokens" ? "aiud-sort-active" : ""} />
                    </th>
                    <th onClick={() => toggleSort("lastActive")} className="aiud-sortable">
                      <span>Last Active</span>
                      <ArrowUpDown size={11} className={userSortKey === "lastActive" ? "aiud-sort-active" : ""} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((u, idx) => {
                    const displayName = u.name || "Unknown user";
                    return (
                      <tr key={idx}>
                        <td>
                          <div className="aiud-user-cell">
                            <div className={`aiud-avatar aiud-avatar-${idx % 5}`}>{getInitials(displayName)}</div>
                            <div className="aiud-user-name">{displayName}</div>
                          </div>
                        </td>
                        <td className="aiud-num">{formatNumber(u.requests)}</td>
                        <td className="aiud-num">{formatNumber(u.tokens)}</td>
                        <td className="aiud-muted">
                          {u.lastActive ? new Date(u.lastActive).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {userUsage.length >= USER_PAGE_SIZE || userPage > 0 ? (
                <div className="aiud-pagination">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={userPage === 0}
                    onClick={() => setUserPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="aiud-page-info">
                    Page {userPage + 1}
                    {userUsageTotal > 0 && ` of ${Math.ceil(userUsageTotal / USER_PAGE_SIZE)}`}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={userUsage.length < USER_PAGE_SIZE}
                    onClick={() => setUserPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* ========== Quota Management ========== */}
      <div className="aiud-card">
        <div className="aiud-card-header">
          <div>
            <div className="aiud-card-title">Quota Management</div>
            <div className="aiud-card-subtitle">Configure tenant and per-user usage limits</div>
          </div>
          <Button variant="primary" size="sm" onClick={openAddQuota} className="aiud-primary-btn">
            <Plus size={14} strokeWidth={2.5} /> Add Quota
          </Button>
        </div>
        {quotasLoading ? (
          <TableSkeleton rows={3} cols={8} />
        ) : quotas.length === 0 ? (
          <div className="aiud-empty">
            <Coins size={28} strokeWidth={1.5} />
            <div>No quotas configured yet</div>
            <button className="aiud-empty-cta" onClick={openAddQuota}>
              <Plus size={12} /> Add your first quota
            </button>
          </div>
        ) : (
          <table className="aiud-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Scope</th>
                <th>Feature</th>
                <th className="aiud-num">Max Requests</th>
                <th className="aiud-num">Max Tokens</th>
                <th className="aiud-num">Max Spend (USD)</th>
                <th>Period</th>
                <th className="aiud-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotas.map((q: any) => {
                const qType = q.quota_type || q.quotaType;
                const isTenantScope = qType === "tenant_monthly";
                return (
                  <tr key={q.id}>
                    <td>
                      <span className={`aiud-quota-type ${isTenantScope ? "aiud-quota-type-tenant" : "aiud-quota-type-user"}`}>
                        {formatQuotaType(qType)}
                      </span>
                    </td>
                    <td>
                      {q.user_id || q.userId
                        ? <span className="aiud-scope-user">User #{q.user_id || q.userId}</span>
                        : <span className="aiud-muted">All Users</span>}
                    </td>
                    <td>
                      {q.feature
                        ? <span className="aiud-feature-pill">{formatFeatureName(q.feature)}</span>
                        : <span className="aiud-muted">All Features</span>}
                    </td>
                    <td className="aiud-num">{(q.max_requests || q.maxRequests) ? formatNumber(q.max_requests || q.maxRequests) : <span className="aiud-muted">—</span>}</td>
                    <td className="aiud-num">{(q.max_tokens || q.maxTokens) ? formatNumber(q.max_tokens || q.maxTokens) : <span className="aiud-muted">—</span>}</td>
                    <td className="aiud-num">{
                      (() => {
                        const cap = q.max_estimated_cost_usd ?? q.maxEstimatedCostUsd;
                        if (cap === null || cap === undefined || cap === "") {
                          return <span className="aiud-muted">—</span>;
                        }
                        return `$${Number(cap).toFixed(2)}`;
                      })()
                    }</td>
                    <td>{formatPeriod(q.period)}</td>
                    <td className="aiud-actions-col">
                      <OverlayTrigger placement="top" overlay={<BsTooltip id={`edit-${q.id}`}>Edit quota</BsTooltip>}>
                        <button
                          className="aiud-icon-action aiud-icon-action-edit"
                          onClick={() => openEditQuota(q)}
                          aria-label="Edit quota"
                        >
                          <Pencil size={14} />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger placement="top" overlay={<BsTooltip id={`del-${q.id}`}>Delete quota</BsTooltip>}>
                        <button
                          className="aiud-icon-action aiud-icon-action-delete"
                          onClick={() => confirmDeleteQuota(q.id)}
                          aria-label="Delete quota"
                        >
                          <Trash2 size={14} />
                        </button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ========== Custom Range Modal ========== */}
      <Modal show={showCustom} onHide={() => setShowCustom(false)} centered dialogClassName="aiud-modal">
        <Modal.Header closeButton>
          <Modal.Title>Custom Date Range</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="aiud-form-group">
            <label>Start Date</label>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
          </div>
          <div className="aiud-form-group">
            <label>End Date</label>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustom(false)}>Cancel</Button>
          <Button variant="primary" onClick={applyCustomRange}>Apply</Button>
        </Modal.Footer>
      </Modal>

      {/* ========== Quota Add/Edit Modal ========== */}
      <Modal show={showQuotaModal} onHide={() => setShowQuotaModal(false)} centered dialogClassName="aiud-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingQuota ? "Edit Quota" : "Add Quota"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="aiud-form-group">
            <label>Quota Type</label>
            <Form.Select
              value={quotaForm.quotaType}
              onChange={(e) => setQuotaForm({ ...quotaForm, quotaType: e.target.value })}
            >
              <option value="user_daily">User Daily</option>
              <option value="tenant_monthly">Tenant Monthly</option>
            </Form.Select>
          </div>
          <div className="aiud-form-group">
            <label>User ID <span className="aiud-form-hint">(blank = all users)</span></label>
            <input
              type="number"
              value={quotaForm.userId ?? ""}
              placeholder="Leave blank for all users"
              onChange={(e) =>
                setQuotaForm({
                  ...quotaForm,
                  userId: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </div>
          <div className="aiud-form-group">
            <label>Feature <span className="aiud-form-hint">(blank = all features)</span></label>
            <Form.Select
              value={quotaForm.feature || ""}
              onChange={(e) =>
                setQuotaForm({ ...quotaForm, feature: e.target.value || null })
              }
            >
              <option value="">All Features</option>
              {FEATURE_KEYS.map((fk) => (
                <option key={fk} value={fk}>{formatFeatureName(fk)}</option>
              ))}
            </Form.Select>
          </div>
          <div className="aiud-form-row">
            <div className="aiud-form-group">
              <label>Max Requests</label>
              <input
                type="number"
                value={quotaForm.maxRequests ?? ""}
                placeholder="No limit"
                onChange={(e) =>
                  setQuotaForm({
                    ...quotaForm,
                    maxRequests: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </div>
            <div className="aiud-form-group">
              <label>Max Tokens</label>
              <input
                type="number"
                value={quotaForm.maxTokens ?? ""}
                placeholder="No limit"
                onChange={(e) =>
                  setQuotaForm({
                    ...quotaForm,
                    maxTokens: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </div>
            <div className="aiud-form-group">
              <label>Max Spend (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quotaForm.maxEstimatedCostUsd ?? ""}
                placeholder="No limit"
                onChange={(e) =>
                  setQuotaForm({
                    ...quotaForm,
                    maxEstimatedCostUsd: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
              <span
                style={{
                  display: "block",
                  marginTop: 4,
                  color: "var(--text-muted)",
                  fontSize: 12,
                  lineHeight: 1.4,
                  fontWeight: 400,
                }}
              >
                Hardest billing guardrail — survives model switches that would
                blow through a token-only cap.
              </span>
            </div>
          </div>
          <div className="aiud-form-group">
            <label>Period</label>
            <Form.Select
              value={quotaForm.period}
              onChange={(e) => setQuotaForm({ ...quotaForm, period: e.target.value })}
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuotaModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleQuotaSave} disabled={quotaSaving}>
            {quotaSaving ? "Saving…" : "Save Quota"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========== Delete Confirm Modal ========== */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered dialogClassName="aiud-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this quota? This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteQuota} disabled={deleteLoading}>
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========== Error Modal ========== */}
      <Modal show={!!errorMessage} onHide={() => setErrorMessage(null)} centered dialogClassName="aiud-modal">
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setErrorMessage(null)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AiUsageDashboard;
